// ============================================================================
// L'IDENTITÉ LÉGALE D'UNE SOCIÉTÉ — SIREN, SIRET du siège, code NAF, numéro de TVA intracommunautaire.
//
// POURQUOI CE FICHIER EXISTE. Cette identité descend aux caisses sous `company.*` et se scelle dans chaque pièce
// fiscale. La Console la saisit à la création d'un client et la corrige ensuite : l'écran qui contrôle la saisie et le
// serveur qui l'enregistre appliquent la MÊME règle — écrite deux fois, elle accepterait un jour d'un côté ce que
// l'autre refuse.
//
// ⚠️ LE NUMÉRO DE TVA NE SE SAISIT JAMAIS : il se calcule depuis le SIREN (clé `(12 + 3 × (SIREN mod 97)) mod 97`).
//    Une faute de frappe dans un numéro tapé se scellerait dans des pièces signées.
// ⚠️ UN IDENTIFIANT ENREGISTRÉ SE CORRIGE, IL NE SE RETIRE PAS : un SIREN ou un SIRET vidé ferait descendre une
//    identité amputée à des caisses qui l'avaient complète.
// ============================================================================

/** SIREN de La Poste : ses établissements ne suivent pas la clé de Luhn (somme des chiffres multiple de 5). */
const LA_POSTE_SIREN = '356000000';

/** Clé de Luhn — celle du SIREN et du SIRET. */
function luhnValid(digits: string): boolean {
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let n = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) { n *= 2; if (n > 9) n -= 9; }
    sum += n;
  }
  return sum % 10 === 0;
}

/** Un SIREN valide : neuf chiffres, clé de Luhn juste. */
export function isValidSiren(v: string): boolean {
  return /^\d{9}$/.test(v) && luhnValid(v);
}

/** Un SIRET valide : quatorze chiffres, clé de Luhn juste — ou, pour La Poste, somme des chiffres multiple de 5. */
export function isValidSiret(v: string): boolean {
  if (!/^\d{14}$/.test(v)) return false;
  if (luhnValid(v)) return true;
  if (!v.startsWith(LA_POSTE_SIREN)) return false;
  return [...v].reduce((s, c) => s + Number(c), 0) % 5 === 0;
}

/**
 * Le code NAF sous sa forme officielle INSEE (« 47.71Z »), ou `null` s'il n'en est pas un.
 * ⚠️ « 4771Z » et « 47.71z » sont acceptés et rendus sous la forme officielle : c'est celle de l'annuaire des
 *    entreprises, donc celle que la recherche Sirene remplit.
 */
export function normalizeNaf(v: string): string | null {
  const m = /^(\d{2})\.?(\d{2})([A-Za-z])$/.exec(v.replace(/\s+/g, ''));
  return m ? `${m[1]}.${m[2]}${m[3]!.toUpperCase()}` : null;
}

/** Le numéro de TVA intracommunautaire d'un SIREN valide (« FR59542051180 »), `null` sinon. */
export function intraVatNumber(siren: string): string | null {
  if (!isValidSiren(siren)) return null;
  const key = (12 + 3 * (Number(siren) % 97)) % 97;
  return `FR${String(key).padStart(2, '0')}${siren}`;
}

/**
 * Le numéro de TVA que porte une société, selon son régime — `null` quand elle n'en a pas.
 * ⚠️ EN FRANCHISE EN BASE (art. 293 B du CGI), PAS DE NUMÉRO PAR DÉFAUT : il n'est attribué qu'à la société qui achète ou
 *    vend dans l'Union au-delà des seuils, ou qui opte (BOI-TVA-DECLA-40-10-20 § 30). Calculer celui d'une société qui
 *    n'en a pas en ferait imprimer un faux sur chacun de ses tickets. Attribué, c'est le même numéro, calculé du SIREN.
 */
export function companyVatNumber(siren: string, regime: { franchise: boolean; numberAssigned: boolean }): string | null {
  if (regime.franchise && !regime.numberAssigned) return null;
  return intraVatNumber(siren);
}

/** « 812 447 902 » — un SIREN tel qu'on le lit. Une valeur qui n'en est pas un est rendue telle quelle. */
export function formatSiren(v: string): string {
  return /^\d{9}$/.test(v) ? `${v.slice(0, 3)} ${v.slice(3, 6)} ${v.slice(6)}` : v;
}

/** « 812 447 902 00014 » — un SIRET tel qu'on le lit. */
export function formatSiret(v: string): string {
  return /^\d{14}$/.test(v) ? `${formatSiren(v.slice(0, 9))} ${v.slice(9)}` : v;
}

/**
 * Les chiffres d'un SIRET tel qu'on le tape (« 812 447 902 00014 », « 812.447.902.00014 ») — quatorze au plus.
 * ⚠️ POUR LA SAISIE SEULEMENT : l'écriture garde ce qui n'est pas un chiffre (`normalizeSiret`), pour ne jamais effacer en
 *    silence une valeur qu'on n'a pas comprise.
 */
export function siretDigits(v: string): string {
  return v.replace(/\D/g, '').slice(0, 14);
}

/** Un SIRET tel qu'on l'enregistre : sans espaces ni points ; ce qui reste d'autre est gardé tel qu'il a été tapé. */
export function normalizeSiret(v: string): string {
  return v.replace(/[\s.]/g, '');
}

/** Un n° de TVA tel qu'on l'enregistre et qu'on le compare : en capitales, sans espaces ni points. */
export function normalizeVatNumber(v: string): string {
  return v.replace(/[\s.]/g, '').toUpperCase();
}

/**
 * CE QUE DIT UN SIRET SAISI — d'une fiche client, où rien ne bloque l'enregistrement (une facture se corrige par avoir) :
 * l'écran SIGNALE, il ne refuse pas. La règle est celle de `isValidSiret`, La Poste comprise.
 */
export type SiretCheck =
  | { state: 'empty' }
  | { state: 'partial'; digits: number }
  | { state: 'valid'; siren: string }
  | { state: 'invalid' };

export function siretCheck(v: string): SiretCheck {
  const digits = siretDigits(v);
  if (!digits) return { state: 'empty' };
  if (digits.length < 14) return { state: 'partial', digits: digits.length };
  return isValidSiret(digits) ? { state: 'valid', siren: digits.slice(0, 9) } : { state: 'invalid' };
}

/**
 * CE QUE DIT UN N° DE TVA INTRACOMMUNAUTAIRE SAISI, au regard du SIRET de la même fiche.
 * ⚠️ « VALIDE » SEULEMENT QUAND LA CLÉ SE VÉRIFIE : un numéro français porte sa clé (`(12 + 3 × (SIREN mod 97)) mod 97`), on
 *    la recalcule sur son propre SIREN. Une clé en lettres (ancienne attribution) ou un numéro d'un autre pays de l'Union ne
 *    se vérifient pas ici : ils sont dits « non vérifiés », jamais « valides ».
 * ⚠️ `expected` est le numéro calculé depuis le SIRET quand il est valide — c'est lui qu'on propose d'un geste.
 */
export type VatCheck =
  | { state: 'empty'; expected: string | null }
  | { state: 'valid'; expected: string | null }
  | { state: 'mismatch'; expected: string }
  | { state: 'unverified'; expected: string | null }
  | { state: 'foreign'; expected: string | null }
  | { state: 'bad_key'; expected: string | null }
  | { state: 'invalid'; expected: string | null };

export function vatCheck(vat: string, siret: string): VatCheck {
  const s = siretCheck(siret);
  const expected = s.state === 'valid' ? intraVatNumber(s.siren) : null;
  const v = normalizeVatNumber(vat);
  if (!v) return { state: 'empty', expected };
  const fr = /^FR([0-9A-Z]{2})(\d{9})$/.exec(v);
  if (fr) {
    if (expected && v !== expected) return { state: 'mismatch', expected };
    if (!/^\d{2}$/.test(fr[1]!)) return { state: 'unverified', expected };
    return intraVatNumber(fr[2]!) === v ? { state: 'valid', expected } : { state: 'bad_key', expected };
  }
  if (/^(?!FR)[A-Z]{2}[0-9A-Z]{2,12}$/.test(v)) return { state: 'foreign', expected };
  return { state: 'invalid', expected };
}

/** Ce qu'on saisit de l'identité d'un client — les chaînes telles que tapées, le vide valant « non renseigné ». */
export interface CompanyIdentityDraft {
  name: string;
  siren: string;
  siretHq: string;
  naf: string;
  zip: string;
  email: string;
}

export type CompanyIdentityField = keyof CompanyIdentityDraft;

/**
 * Ce qui ne va pas dans une saisie, champ par champ — un objet vide quand tout est bon.
 * `recorded` est l'identité déjà enregistrée : absente à la création, elle interdit de vider un identifiant à la
 * modification.
 */
export function companyIdentityProblems(
  draft: CompanyIdentityDraft,
  recorded?: { siren?: string | null; siretHq?: string | null },
): Partial<Record<CompanyIdentityField, string>> {
  const p: Partial<Record<CompanyIdentityField, string>> = {};
  const siren = draft.siren.trim();
  const siret = draft.siretHq.trim();
  if (!draft.name.trim()) p.name = 'La raison sociale est obligatoire.';

  if (!siren) {
    if (recorded?.siren) p.siren = 'Un SIREN enregistré se corrige, il ne se retire pas.';
  } else if (!/^\d{9}$/.test(siren)) p.siren = 'Le SIREN compte 9 chiffres.';
  else if (!isValidSiren(siren)) p.siren = 'Clé de contrôle du SIREN invalide : vérifiez la saisie.';

  if (!siret) {
    if (recorded?.siretHq) p.siretHq = 'Un SIRET enregistré se corrige, il ne se retire pas.';
  } else if (!/^\d{14}$/.test(siret)) p.siretHq = 'Le SIRET compte 14 chiffres.';
  else if (!siren) p.siretHq = 'Renseignez d’abord le SIREN.';
  else if (siret.slice(0, 9) !== siren) p.siretHq = `Le SIRET du siège commence par le SIREN (${formatSiren(siren)}).`;
  else if (!isValidSiret(siret)) p.siretHq = 'Clé de contrôle du SIRET invalide : vérifiez la saisie.';

  if (draft.naf.trim() && !normalizeNaf(draft.naf)) p.naf = 'Quatre chiffres et une lettre, par exemple 47.71Z.';
  if (draft.zip.trim() && !/^\d{5}$/.test(draft.zip.trim())) p.zip = 'Le code postal compte 5 chiffres.';
  if (draft.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())) p.email = 'Adresse e-mail invalide.';
  return p;
}
